import * as React from 'react';
import {useEffect, useState} from 'react';
import {Button, Divider, makeStyles, Spinner, Text, tokens,} from '@fluentui/react-components';
import {ArrowClockwise28Filled, ArrowLeftRegular, ArrowUndoRegular, MailTemplateRegular,} from '@fluentui/react-icons';
import {useRouter} from "../Router";
import apiClient from "../service/apiClient";
import {TemplateItemDetail} from "../../types/types";
import AppBadge from "../components/AppBadge";
import EmailPreview from "../components/EmailPreview";
import ErrorView from "../components/ErrorView";
import {useTranslation} from "react-i18next";
import Page from '../components/Page';
import LanguagePicker from "../components/LanguagePicker";
import i18n from "i18next";
import {Language} from "../../i18n";
import {useTemplateDetailStore} from "../stores/templateDetailStore";
import {getHtmlBodyAsync, getSubjectAsync} from "../taskpane";

interface TemplateDetailPageProps {
    templateId: string;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    title: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
    },
    tags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalXS,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    sectionLabel: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    body: {
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase400,
        whiteSpace: 'pre-wrap',
    },
    insertButton: {
        marginTop: tokens.spacingVerticalS,
    },
    taskBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingBottom: tokens.spacingVerticalS,
        gap: tokens.spacingHorizontalS
    },
    centerAlign: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
    },
});

export default function TemplateDetailPage(props: Readonly<TemplateDetailPageProps>) {
    const {t} = useTranslation();
    const styles = useStyles();
    const {setPage} = useRouter();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [template, setTemplate] = useState<TemplateItemDetail>(null);
    const [error, setError] = useState<unknown>(null);
    const [localLanguage, setLocalLanguage] = useState<Language>(i18n.language as Language);

    const {previousBody, previousSubject, setPrevious, clear} = useTemplateDetailStore();

    useEffect(() => void loadEmailTemplate(), []);

    async function onLocalLanguageChange(language: Language) {
        setLocalLanguage(language);
        await loadEmailTemplate(language);
    }

    async function loadEmailTemplate(language: Language = localLanguage) {
        if (isLoading) return;

        setIsLoading(true);
        setTemplate(null);
        setError(null);

        try {
            const response = await apiClient.get(`/template/${props.templateId}`, {languageOverride: language});
            setTemplate(response.data);
        } catch (err) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }

    // todo err handling
    async function handleInsert() {
        const item = Office.context.mailbox.item as Office.MessageCompose | null;
        if (!item) return;

        let body: string;
        let subject: string;
        try {
            [body, subject] = await Promise.all([getHtmlBodyAsync(item), getSubjectAsync(item)]);
        } catch (err) {
            console.error('Failed to capture current body/subject before insert:', err);
            return;
        }

        setPrevious(body, subject);

        item.body.prependAsync(template.content, {coercionType: Office.CoercionType.Html}, asyncResult => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                console.error('Error prepending content:', asyncResult.error.message);
            }
        });

        if (template.subject) {
            item.subject.setAsync(template.subject, asyncResult => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    console.error('Error setting subject:', asyncResult.error.message);
                }
            });
        }
    }

    function handleUndo() {
        if (previousBody === null && previousSubject === null) return;

        const item = Office.context.mailbox.item as Office.MessageCompose | null;
        if (!item) return;

        item.body.setAsync(previousBody, {coercionType: Office.CoercionType.Html}, (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.error('Error restoring body:', result.error.message);
            }
        });

        if (previousSubject !== null) {
            item.subject.setAsync(previousSubject, (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                    console.error('Error restoring subject:', result.error.message);
                }
            });
        }

        clear();
    }

    return (
        <Page>
            <div className={styles.taskBar}>
                <Button
                    icon={<ArrowClockwise28Filled/>}
                    appearance="subtle"
                    onClick={() => void loadEmailTemplate()}
                    disabled={isLoading}
                    aria-label="Refresh"
                />
                <div style={{flex: 1}}/>
                <Button appearance="subtle" size="small" icon={<ArrowLeftRegular/>}
                        onClick={() => setPage({kind: 'list'})}>
                    {t("translation:general.back")}
                </Button>
            </div>

            {
                (!template || isLoading) &&
                <div className={styles.centerAlign}>
                    <Spinner size="medium"/>
                </div>
            }

            {
                error &&
                <div className={styles.centerAlign}>
                    <ErrorView error={error} onRetry={loadEmailTemplate}/>
                </div>
            }

            {
                template &&
                <div className={styles.root}>
                    {template.title && <Text className={styles.title}>{template.title}</Text>}

                    {template.tags && <div className={styles.tags}>
                        {template.tags.map((tag) => (<AppBadge key={`tag_${tag}`}>{tag}</AppBadge>))}
                    </div>}

                    <Divider/>

                    {/*todo change label to be e-mail language and create new endpoint that straight away has translated subject and content*/}
                    <LanguagePicker value={localLanguage} onChange={onLocalLanguageChange} label={t("translation:general.templateLanguage")} />

                    <div className={styles.section}>
                        <Text className={styles.body}>{template.description}</Text>
                    </div>

                    <div className={styles.section}>
                        <Text className={styles.sectionLabel}>{t("translation:templates.preview")}</Text>
                        <EmailPreview content={template.content} subject={template.subject}
                                      maxHeight={'300px'}/>
                    </div>

                    {!previousBody && (
                        <Button
                            className={styles.insertButton}
                            appearance="primary"
                            icon={<MailTemplateRegular/>}
                            onClick={handleInsert}
                        >
                            {t("translation:templates.insert")}
                        </Button>
                    )}

                    {previousBody && (
                        <Button
                            className={styles.insertButton}
                            appearance="outline"
                            icon={<ArrowUndoRegular/>}
                            onClick={handleUndo}
                        >
                            {t("translation:general.undo")}
                        </Button>
                    )}
                </div>
            }
        </Page>
    );
}
